import {APICallState} from '@eCommerce/frontend-legacy-shared-library-native';
import {PayloadAction, createSlice} from '@reduxjs/toolkit';
import {API} from 'services/api';
import {ResponseError} from 'services/api/api';
import {
  PaymentIntegrationDeleteOptions,
  PaymentIntegrationSaveOptions,
} from 'services/api/paymentintegration';
import {AuthActions} from 'store/auth/auth.reducer';

export interface PaymentCardInfo {
  cardMask: string;
  cardExpiration: string;
  cardNickname?: string;
  cardIssuer: string;
  cardExpired?: boolean;
}

export interface PaymentsState {
  savedCards: PaymentCardInfo[];
  saveCardCallState: APICallState;
  saveCardResponseError?: ResponseError;
  getAllCardsCallState: APICallState;
  deleteCardCallState: APICallState;
  sessionKeyInvalid: boolean;
}

export const initialState: PaymentsState = {
  savedCards: [],
  saveCardCallState: APICallState.INITIAL,
  getAllCardsCallState: APICallState.INITIAL,
  deleteCardCallState: APICallState.INITIAL,
  sessionKeyInvalid: false,
  saveCardResponseError: undefined,
};

const {actions: PaymentsActions, reducer: PaymentsReducer} = createSlice({
  name: 'payments',
  initialState,
  reducers: {
    getAllCards(state) {
      state.getAllCardsCallState = APICallState.LOADING;
    },
    getAllCardsSuccess(state, action: PayloadAction<PaymentCardInfo[]>) {
      state.getAllCardsCallState = APICallState.SUCCESS;
      state.savedCards = action.payload;
    },
    getAllCardsFailure(state) {
      state.getAllCardsCallState = APICallState.ERROR;
    },
    saveCard(state, _action: PayloadAction<PaymentIntegrationSaveOptions>) {
      state.saveCardResponseError = undefined;
      state.saveCardCallState = APICallState.LOADING;
    },
    saveCardSuccess(
      state,
      _action: PayloadAction<PaymentIntegrationSaveOptions>
    ) {
      state.saveCardCallState = APICallState.SUCCESS;
      const cardToSave: PaymentCardInfo = {
        cardMask: _action.payload.cardMask,
        cardExpiration: _action.payload.cardExpiration,
        cardNickname: _action.payload.cardNickname,
        cardIssuer: _action.payload.cardIssuer,
        cardExpired: false,
      };
      state.savedCards.push(cardToSave);
    },
    saveCardFailure(state, action: PayloadAction<ResponseError | undefined>) {
      state.saveCardResponseError = action.payload;
      state.saveCardCallState = APICallState.ERROR;
    },
    setSessionKeyInvalid(state, action: PayloadAction<boolean>) {
      state.sessionKeyInvalid = action.payload;
    },
    deleteCard(state, _action: PayloadAction<PaymentIntegrationDeleteOptions>) {
      state.deleteCardCallState = APICallState.LOADING;
    },
    deleteCardSuccess(
      state,
      action: PayloadAction<PaymentIntegrationDeleteOptions>
    ) {
      const existingCard = state.savedCards.find(
        card =>
          card.cardMask === action.payload.cardMask &&
          card.cardExpiration === action.payload.cardExpiration
      );

      if (existingCard) {
        const index = state.savedCards.indexOf(existingCard);
        if (index > -1) {
          state.savedCards.splice(index, 1);
        }
      }

      state.deleteCardCallState = APICallState.SUCCESS;
    },
    deleteCardFailure(state) {
      state.deleteCardCallState = APICallState.ERROR;
    },
    resetSaveCardCallState(state) {
      state.saveCardCallState = APICallState.INITIAL;
      state.saveCardResponseError = undefined;
    },
    resetDeleteCardCallState(state) {
      state.deleteCardCallState = APICallState.INITIAL;
    },
  },
  extraReducers: builder => {
    builder.addCase(AuthActions.logout.type, () => initialState);
  },
});

export {PaymentsActions, PaymentsReducer};
export default PaymentsReducer;
